'use client';

import { CaretUp } from 'assets/icons';
import React, { useEffect, useRef, useState } from 'react';
import { CarousalProps } from './carouselTypes';
import { useWindowSize } from 'hooks/dom/useWindowSize';
import { VideoForCarousal } from './VIdeoForCarousal';
import styles from './TabControlledCarousel.module.css';

interface TabControlledCarouselProps {
  items: Array<{
    id: string;
    image: string;
    icon: string | React.ReactNode;
    alt: string;
    video: string;
    video_mbl: string;
  }>;
}

const TabsSection = ({
  tabs_data,
  currentIndex,
  goToSlide,
}: {
  tabs_data: CarousalProps[];
  currentIndex: number;
  goToSlide: (index: number) => void;
}) => {
  const FloaterRef = useRef<HTMLSpanElement | null>(null);
  const TabsRootRef = useRef<HTMLDivElement | null>(null);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  // const [previousIndex, setPreviousIndex] = useState(currentIndex);
  const { width } = useWindowSize();
  const isMobile = width < 992;

  // Initialize refs array
  useEffect(() => {
    tabRefs.current = tabRefs.current.slice(0, tabs_data.length);
  }, [tabs_data]);

  // Calculate tab positions and set floater
  const updateFloaterPosition = (targetIndex: number) => {
    if (!FloaterRef.current || !TabsRootRef.current) return;

    // Get tab elements
    const tabElements = tabRefs.current.filter(Boolean);
    const rootRect = TabsRootRef.current.getBoundingClientRect();

    if (
      targetIndex >= 0 &&
      targetIndex < tabElements.length &&
      tabElements[targetIndex]
    ) {
      const targetTab = tabElements[targetIndex];
      if (!targetTab) return;
      // Get target tab dimensions
      const targetRect = targetTab.getBoundingClientRect();

      if (isMobile) {
        // Use original mobile logic with fixed values
        const activeTabWidth = 149.5;
        const floaterHeight = targetRect.height - 8;

        // Calculate position for mobile
        let leftPosition = 0;
        const tabGap = 1.5; // Gap between tabs
        const inactiveTabWidth = 48; // Width of inactive tabs on mobile

        // For the left position, calculate based on the indices
        if (targetIndex === 0) {
          leftPosition = 0;
        } else {
          // For other tabs, calculate based on tabs before it
          for (let i = 0; i < targetIndex; i++) {
            leftPosition += inactiveTabWidth + tabGap;
          }
        }

        // Set the floater position and size for mobile
        FloaterRef.current.style.width = `${activeTabWidth}px`;
        FloaterRef.current.style.height = `${floaterHeight}px`;
        FloaterRef.current.style.left = `${leftPosition}px`;
      } else {
        // Desktop behavior - use actual measured values with spacing
        const floaterWidth = targetRect.width - 8; // Subtract for padding
        const floaterHeight = targetRect.height - 8;

        // Calculate position for desktop
        const leftPosition = targetRect.left - rootRect.left + 4; // Add offset for padding

        // Set the floater position and size for desktop
        FloaterRef.current.style.width = `${floaterWidth}px`;
        FloaterRef.current.style.height = `${floaterHeight}px`;
        FloaterRef.current.style.left = `${leftPosition}px`;
      }
    }
  };

  // Set initial floater position
  useEffect(() => {
    // Add a small delay to ensure DOM has updated
    const timeoutId = setTimeout(() => {
      updateFloaterPosition(currentIndex);
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [currentIndex, width]);

  useEffect(() => {
    const handleResize = () => {
      updateFloaterPosition(currentIndex);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [currentIndex]);

  const handleTabClick = (index: number) => {
    // Store the previous index for animation calculations
    // setPreviousIndex(currentIndex);

    // Update the floater position
    updateFloaterPosition(index);

    // Update current slide
    goToSlide(index);
  };

  return (
    <div className={styles.tabsContainer}>
      <div className={styles.tabsBar} ref={TabsRootRef}>
        <span className={styles.floaterSpan} ref={FloaterRef} />
        {tabs_data.map((tab, index) => (
          <button
            className={styles.tab}
            key={index}
            data-active={index === currentIndex}
            onClick={() => handleTabClick(index)}
            ref={(el) => {
              tabRefs.current[index] = el;
            }}
          >
            {tab?.icon && tab?.alt ? (
              <div className={styles.iconContainer}>
                {/* <Image
                  src={tab.icon}
                  alt={tab.alt}
                  width={isMobile ? 14 : 16}
                  height={isMobile ? 14 : 16}
                  priority={true}
                /> */}
                {tab.icon}
              </div>
            ) : null}
            {!isMobile || (index === currentIndex && tab.text) ? (
              <h5>{tab.text}</h5>
            ) : null}
          </button>
        ))}
      </div>
    </div>
  );
};

const TabControlledCarousel: React.FC<TabControlledCarouselProps> = ({
  items,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionEnabled, setTransitionEnabled] = useState(true);

  // Function to handle forward movement (next button)
  const handleNext = () => {
    if (isTransitioning) return;

    setIsTransitioning(true);

    if (currentIndex === items.length - 1) {
      // If we're at the last slide, we need special handling
      setCurrentIndex(items.length); // Go to the clone of the first slide

      // After the transition completes, we'll jump to the actual first slide without animation
      setTimeout(() => {
        setTransitionEnabled(false);
        setCurrentIndex(0);
        setIsTransitioning(false);
      }, 500); // Match the transition duration
    } else {
      setCurrentIndex(currentIndex + 1);
      setTimeout(() => {
        setIsTransitioning(false);
      }, 500);
    }
  };

  // Function to handle backward movement (prev button)
  const handlePrev = () => {
    if (isTransitioning) return;

    setIsTransitioning(true);

    if (currentIndex === 0) {
      // If we're at the first slide, we need special handling
      setCurrentIndex(-1); // Go to the clone of the last slide

      // After the transition completes, we'll jump to the actual last slide without animation
      setTimeout(() => {
        setTransitionEnabled(false);
        setCurrentIndex(items.length - 1);
        setIsTransitioning(false);
      }, 500); // Match the transition duration
    } else {
      setCurrentIndex(currentIndex - 1);
      setTimeout(() => {
        setIsTransitioning(false);
      }, 500);
    }
  };

  // Enable transition after a state change
  useEffect(() => {
    if (!transitionEnabled) {
      // Re-enabling transition after state update is processed
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTransitionEnabled(true);
        });
      });
    }
  }, [transitionEnabled, currentIndex]);

  // Function to go to a specific slide
  const goToSlide = (index: number) => {
    if (isTransitioning) return;
    setCurrentIndex(index);
  };

  // Added clones of the first and last slide to create the illusion of infinite looping
  const allSlides = [
    // Clone of the last slide (for seamless backward transition)
    ...items.slice(items.length - 1),
    // Original slides
    ...items,
    // Clone of the first slide (for seamless forward transition)
    ...items.slice(0, 1),
  ];

  return (
    <div className={styles.carouselContainer}>
      <div
        className={styles.carouselTrack}
        style={
          {
            ['--track-transform']: `translateX(-${(currentIndex + 1) * 100}%)`,
            ['--track-transition']: transitionEnabled
              ? 'transform 0.5s ease-in-out'
              : 'none',
          } as React.CSSProperties
        }
      >
        {allSlides.map((item, index) => (
          <div className={styles.carouselSlide} key={`${item.id}-${index}`}>
            {/* <Image src={item.image} alt={item.alt} fill priority={true} /> */}
            <VideoForCarousal
              src={item.video}
              srcResponsive={item.video_mbl}
              videoBorderRadius="16px"
            />
          </div>
        ))}
      </div>

      <button
        className={styles.prevButton}
        onClick={handlePrev}
        aria-label="Previous slide"
      >
        <CaretUp style={{ transform: 'rotate(-90deg)' }} fill="black" />
      </button>
      <button
        className={styles.nextButton}
        onClick={handleNext}
        aria-label="Next slide"
      >
        <CaretUp style={{ transform: 'rotate(90deg)' }} fill="black" />
      </button>
      <TabsSection
        goToSlide={goToSlide}
        tabs_data={items}
        currentIndex={currentIndex}
      />
    </div>
  );
};

export default TabControlledCarousel;
